import { supabase } from '../lib/supabase';
import { useState, useEffect, useContext, createContext, useMemo, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { generateId, generateUUID } from '../utils/helpers';
import { syncProfileToSupabase, syncRoutineToSupabase } from '../services/dataSyncService';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

// Commission tier calculation helper
const calculateCommissionTier = (signups) => {
    if (signups >= 51) return { tier: 4, rate: 0.25, label: '25%' };
    if (signups >= 21) return { tier: 3, rate: 0.15, label: '15%' };
    if (signups >= 11) return { tier: 2, rate: 0.10, label: '10%' };
    if (signups >= 1) return { tier: 1, rate: 0.05, label: '5%' };
    return { tier: 0, rate: 0, label: '0%' };
};

/**
 * Map Supabase snake_case DB columns → app camelCase state.
 * Called every time a profile row is read from Supabase so the app
 * never uses the raw snake_case keys that would silently stay as
 * undefined and fall through to DEFAULT_PROFILE values.
 */
const mapProfileFromDB = (raw) => {
    if (!raw) return {};
    return {
        // identity
        id: raw.id,
        email: raw.email ?? raw.email,
        displayName: raw.display_name ?? raw.displayName ?? '',
        gender: raw.gender ?? 'Female',
        dateOfBirth: raw.date_of_birth ?? raw.dateOfBirth ?? null,
        age: raw.age ?? 30,
        height: raw.height ?? 165,
        weight: raw.weight ?? 65,
        startWeight: raw.start_weight ?? raw.startWeight ?? null,
        goalWeight: raw.goal_weight ?? raw.goalWeight ?? 60,
        units: raw.units ?? 'metric',
        goal: raw.goal ?? '',
        region: raw.region ?? 'Global',
        country: raw.country ?? null,
        // diet & activity
        dietType: raw.diet_type ?? raw.dietType ?? null,
        activityLevel: raw.activity_level ?? raw.activityLevel ?? 'Light',
        workoutLocation: raw.workout_location ?? raw.workoutLocation ?? 'Home',
        workoutDuration: raw.workout_duration ?? raw.workoutDuration ?? 30,
        bodyShape: raw.body_shape ?? raw.bodyShape ?? null,
        // health & safety
        // targets
        dailyCalorieTarget: raw.daily_calorie_target ?? raw.dailyCalorieTarget ?? 1800,
        waterGoal: raw.water_goal ?? raw.waterGoal ?? 2500,
        stepsGoal: raw.steps_goal ?? raw.stepsGoal ?? 10000,
        // schedule
        sleepStart: raw.sleep_start ?? raw.sleepStart ?? 23,
        sleepEnd: raw.sleep_end ?? raw.sleepEnd ?? 7,
        // journey dates
        journeyStartDate: raw.journey_start_date ?? raw.journeyStartDate ?? null,
        goalDate: raw.goal_date ?? raw.goalDate ?? null,
        goalDateAuto: raw.goal_date_auto ?? raw.goalDateAuto ?? null,
        // subscription & trial
        subscriptionTier: raw.subscription_tier ?? raw.subscriptionTier ?? 'STARTER',
        trialActive: raw.trial_active ?? raw.trialActive ?? false,
        trialStartDate: raw.trial_start_date ?? raw.trialStartDate ?? null,
        trialDurationDays: raw.trial_duration_days ?? raw.trialDurationDays ?? 0,
        trialType: raw.trial_type ?? raw.trialType ?? null,
        referredBy: raw.referred_by ?? raw.referredBy ?? null,
        // scans
        scansUsed: raw.scans_used ?? raw.scansUsed ?? 0,
        scanLimit: raw.scan_limit ?? raw.scanLimit ?? 10,
        // referral
        referralTrack: raw.referral_track ?? raw.referralTrack ?? 'Standard',
        referralStats: raw.referral_stats ?? raw.referralStats ?? null,
        // identity verification
        identityVerified: raw.identity_verified ?? raw.identityVerified ?? false,
        isSuperAdmin: raw.is_super_admin ?? raw.isSuperAdmin ?? false,
        verifiedEmail: raw.verified_email ?? raw.verifiedEmail ?? null,
        verifiedPhone: raw.verified_phone ?? raw.verifiedPhone ?? null,
        // preferences
        theme: raw.theme ?? 'dark',
        notificationsEnabled: raw.notifications_enabled ?? raw.notificationsEnabled ?? true,
        createdAt: raw.created_at ?? raw.createdAt ?? new Date(),
        // favorites (JSONB in Supabase)
        favorites: raw.favorites ?? { meals: [], exercises: [], routines: [] },
    };
};

/**
 * Calculate optimal goal date based on safe weight change rates
 * - Weight loss: 0.5kg/week (safe, sustainable)
 * - Weight gain/muscle: 0.25kg/week (lean gains)
 * - Maintain: No date needed
 */
export const calculateOptimalGoalDate = (currentWeight, goalWeight, goal) => {
    if (!currentWeight || !goalWeight || currentWeight === goalWeight) {
        return null;
    }

    const weightDiff = Math.abs(goalWeight - currentWeight);
    let weeksNeeded;

    if (goal === 'Lose Weight' || goal === 'Aggressive Loss') {
        // Safe loss: 0.5kg/week (1lb/week)
        weeksNeeded = Math.ceil(weightDiff / 0.5);
    } else if (goal === 'Gain Weight' || goal === 'Build Muscle') {
        // Safe gain: 0.25kg/week (0.5lb/week) for lean mass
        weeksNeeded = Math.ceil(weightDiff / 0.25);
    } else {
        // Maintain - no specific date
        return null;
    }

    const goalDate = new Date();
    goalDate.setDate(goalDate.getDate() + (weeksNeeded * 7));
    return goalDate.toISOString();
};

const DEFAULT_PROFILE = {
    id: generateUUID(), // Use proper UUID for Supabase compatibility
    displayName: '',
    email: '',
    gender: 'Female',
    dateOfBirth: null,  // NEW: Store DOB instead of static age
    age: 30,            // Calculated from DOB, fallback
    height: 165,
    weight: 65,
    startWeight: null,  // Weight when user started
    goalWeight: 60,
    units: 'metric', // 'metric' (kg, cm, ml) or 'imperial' (lbs, ft/in, oz)
    goal: '',
    dietType: null,     // NEW: Selected diet type from dietDatabase
    activityLevel: 'Light',
    fitnessLevel: 'Beginner',
    workoutLocation: 'Home',
    workoutLocations: ['Home'], // Multi-select support
    workoutDuration: 30,
    dietaryPreferences: [],
    allergens: [],
    cuisinePreferences: [],
    // Health & Safety
    injuries: [],           // Physical injuries: ['lower_back_pain', 'knee_injury', ...]
    medicalConditions: [],  // Health conditions: ['hypertension', 'asthma', ...]
    medications: [],        // Medications affecting metabolism
    region: 'Global',
    country: null,
    dailyCalorieTarget: 1800,
    waterGoal: 2500,
    sleepStart: 23,     // Hour sleep begins (0-23), e.g. 23 = 11 PM
    sleepEnd: 7,         // Hour sleep ends (0-23), e.g. 7 = 7 AM
    stepsGoal: 10000,
    subscriptionTier: 'STARTER',  // Default to free tier
    isSuperAdmin: false,
    theme: 'dark',
    notificationsEnabled: true,
    createdAt: new Date(),
    // Journey Date Management
    journeyStartDate: null,     // When user started their diet journey
    goalDate: null,             // User-set goal date (override)
    goalDateAuto: null,         // Auto-calculated optimal date
    // Trial Management
    trialActive: false,
    trialStartDate: null,
    trialDurationDays: 0,
    trialType: null,
    referredBy: null,
    // Scan Limit (Free tier)
    scansUsed: 0,
    scanLimit: 10,
    // Referral Program Data
    referralTrack: 'Standard',  // Default, not Ambassador
    referralStats: {
        totalClicks: 0,
        totalSignups: 0,
        confirmedSignups: 0,
        totalRevenue: 0,
        totalEarnings: 0,
        pendingEarnings: 0,
        paidEarnings: 0,
        history: [],
        referrals: []
    },
    // Identity Verification
    identityVerified: false,
    verifiedEmail: null,
    verifiedPhone: null,
    // Favorites Storage
    favorites: {
        meals: [],      // { id, meal, originalMeal?, fusionCuisine?, fusionLevel?, addedAt }
        exercises: [],  // { id, exercise, customDuration?, addedAt }
        routines: []    // { id, name, exercises[], totalDuration, totalCalories, addedAt }
    }
};

export const UserProvider = ({ children }) => {
    // User profile state - Cloud source of truth
    const [userProfile, setUserProfileRaw] = useState(DEFAULT_PROFILE);
    const [language, setLanguageRaw] = useLocalStorage('calobite_language', 'en'); // UI pref can stay local or move to DB

    const [theme, setThemeRaw] = useLocalStorage('calobite_theme', 'dark');

    // Loading state
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    // UI State - Non-persistent dismissed reminders
    const [dismissedReminders, setDismissedReminders] = useState(new Set());
    const dismissReminder = useCallback((id) => setDismissedReminders(prev => new Set([...prev, id])), []);
    const isReminderDismissed = useCallback((id) => dismissedReminders.has(id), [dismissedReminders]);

    // Track which user is currently logged in so we reload when it changes
    const [currentEmail, setCurrentEmail] = useState(() =>
        localStorage.getItem('calobite_current_user')
    );

    // Fetch profile from Supabase on mount/auth change AND on user switch
    useEffect(() => {
        let mounted = true;

        async function loadProfile() {
            // Guard: if supabase is not configured, skip
            if (!supabase) {
                console.warn('[UserContext] Supabase not configured, using defaults');
                if (mounted) setIsLoadingProfile(false);
                return;
            }

            // ⛔ SECURITY: Reset to safe defaults before loading new profile.
            // This prevents the previous user's isSuperAdmin from lingering while
            // the async fetch is in flight.
            if (mounted) {
                setUserProfileRaw({ ...DEFAULT_PROFILE });
            }

            try {
                let profileData = null;

                // Method 1: Try Supabase Auth session (for users who signed up via Supabase Auth)
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const { data, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', user.id)
                        .single();
                    if (data) profileData = data;
                }

                // Method 2: Fall back to localStorage email (for our custom email/password login)
                if (!profileData) {
                    const storedEmail = localStorage.getItem('calobite_current_user');
                    if (storedEmail) {
                        console.log('[UserContext] No Supabase Auth user, trying localStorage email:', storedEmail);
                        const { data, error } = await supabase
                            .from('profiles')
                            .select('*')
                            .eq('email', storedEmail.toLowerCase())
                            .single();
                        if (data) {
                            profileData = data;
                            console.log('[UserContext] ✅ Profile loaded via email lookup');
                        }
                    }
                }

                if (profileData && mounted) {
                    // Map DB snake_case → app camelCase, then merge with defaults for any missing fields
                    setUserProfileRaw({ ...DEFAULT_PROFILE, ...mapProfileFromDB(profileData) });
                } else if (!profileData && mounted) {
                    // No profile yet, likely new user or error
                    console.log('No profile found, using defaults');
                    setUserProfileRaw({ ...DEFAULT_PROFILE });
                }
            } catch (err) {
                console.error('Error loading profile:', err);
            } finally {
                if (mounted) setIsLoadingProfile(false);
            }
        }

        loadProfile();

        // Subscribe to realtime changes (only if supabase is configured)
        let channel = null;
        if (supabase) {
            channel = supabase.channel('profile_changes')
                .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, payload => {
                    if (payload.new && mounted) {
                        // Check if it's for current user? RLS should handle, but payload filtering is good
                        supabase.auth.getUser().then(({ data: { user } }) => {
                            if (user && payload.new.id === user.id) {
                                // Map realtime snake_case payload → camelCase before merging
                                setUserProfileRaw(prev => ({ ...prev, ...mapProfileFromDB(payload.new) }));
                            }
                        });
                    }
                })
                .subscribe();
        }

        // Listen for user switches: custom event (Capacitor iOS) + storage (browser tabs)
        const handleAuthChanged = (e) => {
            // Custom event carries the new email directly; storage event uses the key
            const newEmail = e?.detail?.email !== undefined
                ? e.detail.email
                : (e.key === 'calobite_current_user' ? e.newValue : null);

            if (newEmail !== undefined && newEmail !== currentEmail) {
                console.log('[UserContext] 🔄 Auth changed, reloading profile:', currentEmail, '->', newEmail);
                if (!newEmail) {
                    // Logged out — reset immediately to safe defaults (clears isSuperAdmin)
                    setUserProfileRaw({ ...DEFAULT_PROFILE });
                }
                setCurrentEmail(newEmail);
            }
        };
        window.addEventListener('calobite_auth_changed', handleAuthChanged);
        window.addEventListener('storage', handleAuthChanged);

        return () => {
            mounted = false;
            if (channel && supabase) {
                supabase.removeChannel(channel);
            }
            window.removeEventListener('calobite_auth_changed', handleAuthChanged);
            window.removeEventListener('storage', handleAuthChanged);
        };
    }, [currentEmail]);

    // Check trial expiration on mount and profile changes
    useEffect(() => {
        if (userProfile.trialActive && userProfile.trialStartDate) {
            const trialStart = new Date(userProfile.trialStartDate);
            const now = new Date();
            const daysPassed = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));

            if (daysPassed >= userProfile.trialDurationDays) {
                setUserProfileRaw(prev => ({
                    ...prev,
                    subscriptionTier: 'Free',
                    trialActive: false
                }));
            }
        }
    }, [userProfile.trialActive, userProfile.trialStartDate]);

    // Initialize app after profile load
    useEffect(() => {
        if (!isLoadingProfile) {
            setIsInitialized(true);
        }
    }, [isLoadingProfile]);

    // Apply theme to document
    useEffect(() => {
        const currentTheme = userProfile.theme || 'dark';
        document.documentElement.setAttribute('data-theme', currentTheme);

        // Apply theme-specific body classes
        document.body.classList.remove('theme-dark', 'theme-light', 'theme-neon');
        document.body.classList.add(`theme-${currentTheme}`);
    }, [userProfile.theme]);

    // Unified profile updater - supports BOTH object and function syntax
    // Also syncs to Supabase in background
    const setUserProfile = useCallback((updates) => {
        setUserProfileRaw(prev => {
            // If updates is a function, call it with current state
            let newProfile;
            if (typeof updates === 'function') {
                newProfile = updates(prev);
            } else {
                // If updates is an object, merge it with current state
                newProfile = { ...prev, ...updates };
            }

            // Sync to Supabase in background (non-blocking)
            syncProfileToSupabase(newProfile).catch(err =>
                console.warn('[UserContext] Profile sync failed:', err)
            );

            return newProfile;
        });
    }, [setUserProfileRaw]);

    // Language setter with document attribute
    const setLanguage = useCallback((lang) => {
        setLanguageRaw(lang);
        document.documentElement.setAttribute('lang', lang);
        // Also set text direction
        const isRTL = ['ar', 'he', 'fa', 'ur'].includes(lang);
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }, [setLanguageRaw]);

    // Theme setter
    const setTheme = useCallback((newTheme) => {
        setThemeRaw(newTheme);
        setUserProfile({ theme: newTheme });
    }, [setThemeRaw, setUserProfile]);

    // Calculate current commission rate
    const getCommissionRate = useCallback(() => {
        if (userProfile.referralTrack === 'Ambassador') {
            return { rate: 0.30, label: '30%', tier: 'Ambassador' };
        }
        return calculateCommissionTier(userProfile.referralStats?.confirmedSignups || 0);
    }, [userProfile.referralTrack, userProfile.referralStats?.confirmedSignups]);

    // Add a new referral
    const addReferral = useCallback((referralData) => {
        const newReferral = {
            id: generateId(),
            userId: referralData.userId,
            email: referralData.email,
            signupDate: new Date(),
            trialType: userProfile.referralTrack === 'Ambassador' ? 'ambassador' : 'standard',
            trialDays: userProfile.referralTrack === 'Ambassador' ? 30 : 7,
            status: 'trial',
            revenue: 0,
            commission: 0
        };

        setUserProfile(prev => ({
            ...prev,
            referralStats: {
                ...prev.referralStats,
                totalSignups: (prev.referralStats?.totalSignups || 0) + 1,
                referrals: [...(prev.referralStats?.referrals || []), newReferral]
            }
        }));
    }, [userProfile.referralTrack, setUserProfile]);

    // Increment scan counter
    const incrementScans = useCallback(() => {
        if (userProfile.subscriptionTier === 'Free') {
            setUserProfile(prev => ({
                ...prev,
                scansUsed: (prev.scansUsed || 0) + 1
            }));
        }
    }, [userProfile.subscriptionTier, setUserProfile]);

    // Check if user can scan
    const canScan = useCallback(() => {
        if (userProfile.subscriptionTier !== 'Free') return true;
        return (userProfile.scansUsed || 0) < (userProfile.scanLimit || 10);
    }, [userProfile.subscriptionTier, userProfile.scansUsed, userProfile.scanLimit]);

    // Verify identity
    const verifyIdentity = useCallback((email, phone) => {
        setUserProfile({
            identityVerified: true,
            verifiedEmail: email,
            verifiedPhone: phone
        });
    }, [setUserProfile]);

    // ===== FAVORITES MANAGEMENT =====

    // Add item to favorites (type: 'meals', 'exercises', 'routines')
    const addToFavorites = useCallback((type, item) => {
        const id = generateId();
        const favoriteItem = {
            id,
            ...item,
            addedAt: new Date().toISOString()
        };

        setUserProfile(prev => ({
            ...prev,
            favorites: {
                ...prev.favorites,
                [type]: [...(prev.favorites?.[type] || []), favoriteItem]
            }
        }));
        return id;
    }, [setUserProfile]);

    // Remove item from favorites
    const removeFromFavorites = useCallback((type, itemId) => {
        setUserProfile(prev => ({
            ...prev,
            favorites: {
                ...prev.favorites,
                [type]: (prev.favorites?.[type] || []).filter(item => item.id !== itemId)
            }
        }));
    }, [setUserProfile]);

    // Check if item is in favorites (by name for meals/exercises)
    const isFavorite = useCallback((type, itemName) => {
        const favorites = userProfile.favorites?.[type] || [];
        if (type === 'meals') {
            return favorites.find(f => f.meal?.name === itemName);
        } else if (type === 'exercises') {
            return favorites.find(f => f.exercise?.name === itemName);
        } else if (type === 'routines') {
            return favorites.find(f => f.name === itemName);
        }
        return null;
    }, [userProfile.favorites]);

    // Update a favorite item (for fusion adjustments)
    const updateFavorite = useCallback((type, itemId, updates) => {
        setUserProfile(prev => ({
            ...prev,
            favorites: {
                ...prev.favorites,
                [type]: (prev.favorites?.[type] || []).map(item =>
                    item.id === itemId ? { ...item, ...updates } : item
                )
            }
        }));
    }, [setUserProfile]);

    // Save meal with fusion info
    const saveMealToFavorites = useCallback((meal, originalMeal = null, fusionCuisine = null, fusionLevel = 50) => {
        return addToFavorites('meals', {
            meal,
            originalMeal,
            fusionCuisine,
            fusionLevel
        });
    }, [addToFavorites]);

    // Save routine to favorites\n    // Save routine to favorites (with unique name validation + Supabase sync)
    const saveRoutineToFavorites = useCallback((name, exercises, totalDuration, totalCalories) => {
        // Check if routine name already exists
        const routines = userProfile.favorites?.routines || [];
        if (routines.some(r => r.name.toLowerCase().trim() === name.toLowerCase().trim())) {
            return { success: false, error: 'A routine with this name already exists' };
        }

        const routineId = addToFavorites('routines', {
            name,
            exercises,
            totalDuration,
            totalCalories
        });

        // Sync to Supabase in background
        syncRoutineToSupabase({
            id: routineId,
            name,
            exercises,
            totalDuration,
            totalCalories
        }).catch(err => console.warn('[UserContext] Routine sync failed:', err));

        return { success: true };
    }, [addToFavorites, userProfile.favorites?.routines]);

    // Rename a routine (with unique name validation)
    const renameRoutine = useCallback((routineId, newName) => {
        // Check if name already exists (excluding current routine)
        const routines = userProfile.favorites?.routines || [];
        const nameExists = routines.some(r =>
            r.id !== routineId && r.name.toLowerCase().trim() === newName.toLowerCase().trim()
        );
        if (nameExists) {
            return { success: false, error: 'A routine with this name already exists' };
        }
        setUserProfile(prev => ({
            ...prev,
            favorites: {
                ...prev.favorites,
                routines: (prev.favorites?.routines || []).map(routine =>
                    routine.id === routineId ? { ...routine, name: newName } : routine
                )
            }
        }));
        return { success: true };
    }, [setUserProfile, userProfile.favorites?.routines]);

    // Remove an exercise from a routine
    const removeExerciseFromRoutine = useCallback((routineId, exerciseName) => {
        setUserProfile(prev => {
            const routines = prev.favorites?.routines || [];
            const updatedRoutines = routines.map(routine => {
                if (routine.id === routineId) {
                    const updatedExercises = routine.exercises.filter(ex => ex.name !== exerciseName);
                    const totalDuration = updatedExercises.reduce((sum, ex) => sum + (ex.customDuration || ex.duration || 0), 0);
                    const totalCalories = updatedExercises.reduce((sum, ex) => sum + (ex.customCalories || ex.caloriesBurned || 0), 0);
                    return { ...routine, exercises: updatedExercises, totalDuration, totalCalories };
                }
                return routine;
            });
            return {
                ...prev,
                favorites: { ...prev.favorites, routines: updatedRoutines }
            };
        });
    }, [setUserProfile]);

    // Add an exercise to an existing routine
    const addExerciseToRoutine = useCallback((routineId, exercise) => {
        setUserProfile(prev => {
            const routines = prev.favorites?.routines || [];
            const updatedRoutines = routines.map(routine => {
                if (routine.id === routineId) {
                    // Check if exercise already exists
                    if (routine.exercises.some(ex => ex.name === exercise.name)) {
                        return routine; // Don't add duplicates
                    }
                    const updatedExercises = [...routine.exercises, exercise];
                    const totalDuration = updatedExercises.reduce((sum, ex) => sum + (ex.customDuration || ex.duration || 0), 0);
                    const totalCalories = updatedExercises.reduce((sum, ex) => sum + (ex.customCalories || ex.caloriesBurned || 0), 0);
                    return { ...routine, exercises: updatedExercises, totalDuration, totalCalories };
                }
                return routine;
            });
            return {
                ...prev,
                favorites: { ...prev.favorites, routines: updatedRoutines }
            };
        });
    }, [setUserProfile]);

    // Get all saved routines
    const getSavedRoutines = useCallback(() => {
        return userProfile.favorites?.routines || [];
    }, [userProfile.favorites?.routines]);

    // Get all routines that contain a specific exercise
    const getRoutinesContainingExercise = useCallback((exerciseName) => {
        const routines = userProfile.favorites?.routines || [];
        return routines.filter(routine =>
            routine.exercises?.some(ex => ex.name === exerciseName)
        ).map(r => ({ id: r.id, name: r.name }));
    }, [userProfile.favorites?.routines]);

    // Check if exercise is in any routine
    const isExerciseInAnyRoutine = useCallback((exerciseName) => {
        const routines = userProfile.favorites?.routines || [];
        return routines.some(routine =>
            routine.exercises?.some(ex => ex.name === exerciseName)
        );
    }, [userProfile.favorites?.routines]);

    // Check if routine name already exists
    const routineNameExists = useCallback((name) => {
        const routines = userProfile.favorites?.routines || [];
        return routines.some(r => r.name.toLowerCase().trim() === name.toLowerCase().trim());
    }, [userProfile.favorites?.routines]);

    // Create a new empty routine
    const createNewRoutine = useCallback((name) => {
        if (routineNameExists(name)) {
            return { success: false, error: 'Routine name already exists' };
        }
        const newRoutine = {
            id: `routine_${Date.now()}`,
            name: name.trim(),
            exercises: [],
            totalDuration: 0,
            totalCalories: 0,
            createdAt: new Date().toISOString()
        };
        setUserProfile(prev => ({
            ...prev,
            favorites: {
                ...prev.favorites,
                routines: [...(prev.favorites?.routines || []), newRoutine]
            }
        }));
        return { success: true, routine: newRoutine };
    }, [routineNameExists, setUserProfile]);

    // Add exercise to multiple routines at once
    const addExerciseToMultipleRoutines = useCallback((exercise, routineIds) => {
        setUserProfile(prev => {
            const routines = prev.favorites?.routines || [];
            const updatedRoutines = routines.map(routine => {
                if (routineIds.includes(routine.id)) {
                    // Check if exercise already exists
                    if (routine.exercises.some(ex => ex.name === exercise.name)) {
                        return routine; // Don't add duplicates
                    }
                    const updatedExercises = [...routine.exercises, exercise];
                    const totalDuration = updatedExercises.reduce((sum, ex) => sum + (ex.customDuration || ex.duration || 0), 0);
                    const totalCalories = updatedExercises.reduce((sum, ex) => sum + (ex.customCalories || ex.caloriesBurned || 0), 0);
                    return { ...routine, exercises: updatedExercises, totalDuration, totalCalories };
                }
                return routine;
            });
            return {
                ...prev,
                favorites: { ...prev.favorites, routines: updatedRoutines }
            };
        });
    }, [setUserProfile]);

    // Move exercise from one routine to other routines
    const moveExerciseBetweenRoutines = useCallback((exercise, fromRoutineId, toRoutineIds) => {
        setUserProfile(prev => {
            const routines = prev.favorites?.routines || [];
            const updatedRoutines = routines.map(routine => {
                // Remove from source routine
                if (routine.id === fromRoutineId) {
                    const updatedExercises = routine.exercises.filter(ex => ex.name !== exercise.name);
                    const totalDuration = updatedExercises.reduce((sum, ex) => sum + (ex.customDuration || ex.duration || 0), 0);
                    const totalCalories = updatedExercises.reduce((sum, ex) => sum + (ex.customCalories || ex.caloriesBurned || 0), 0);
                    return { ...routine, exercises: updatedExercises, totalDuration, totalCalories };
                }
                // Add to target routines
                if (toRoutineIds.includes(routine.id)) {
                    if (routine.exercises.some(ex => ex.name === exercise.name)) {
                        return routine; // Don't add duplicates
                    }
                    const updatedExercises = [...routine.exercises, exercise];
                    const totalDuration = updatedExercises.reduce((sum, ex) => sum + (ex.customDuration || ex.duration || 0), 0);
                    const totalCalories = updatedExercises.reduce((sum, ex) => sum + (ex.customCalories || ex.caloriesBurned || 0), 0);
                    return { ...routine, exercises: updatedExercises, totalDuration, totalCalories };
                }
                return routine;
            });
            return {
                ...prev,
                favorites: { ...prev.favorites, routines: updatedRoutines }
            };
        });
    }, [setUserProfile]);


    const value = {
        userProfile,
        setUserProfile,
        isInitialized,
        isLoadingProfile,
        language,
        setLanguage,
        theme,
        setTheme,
        getCommissionRate,
        addReferral,
        incrementScans,
        canScan,
        verifyIdentity,
        // Favorites
        addToFavorites,
        removeFromFavorites,
        isFavorite,
        updateFavorite,
        saveMealToFavorites,
        saveRoutineToFavorites,
        // Routine Management
        renameRoutine,
        removeExerciseFromRoutine,
        addExerciseToRoutine,
        getSavedRoutines,
        getRoutinesContainingExercise,
        isExerciseInAnyRoutine,
        routineNameExists,
        createNewRoutine,
        addExerciseToMultipleRoutines,
        moveExerciseBetweenRoutines,
        // UI State (Non-persistent)
        dismissedReminders,
        dismissReminder,
        isReminderDismissed
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};
